/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { syncService } from '../lib/syncService';
import { offlineService } from '../lib/offlineDb';

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  const updatePendingCount = useCallback(async () => {
    try {
      const count = await offlineService.getPendingCount();
      setPendingCount(count);
    } catch (error) {
      console.error('[Offline] Error getting pending count:', error);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      console.log('[Offline] Connection restored');
      setIsOnline(true);
    };

    const handleOffline = () => {
      console.log('[Offline] Connection lost');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    updatePendingCount();

    const unsubscribe = syncService.subscribe(event => {
      switch (event.type) {
        case 'sync_started':
          setIsSyncing(true);
          break;
        case 'sync_completed':
          setIsSyncing(false);
          setLastSyncResult({
            success: true,
            synced: event.synced,
            failed: event.failed,
            timestamp: new Date(),
          });
          updatePendingCount();
          break;
        case 'sync_error':
          setIsSyncing(false);
          setLastSyncResult({
            success: false,
            error: event.error,
            timestamp: new Date(),
          });
          break;
        case 'queued':
        case 'action_synced':
        case 'action_failed':
          updatePendingCount();
          break;
      }
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, [updatePendingCount]);

  const manualSync = useCallback(async () => {
    if (!isOnline) {
      return { success: false, reason: 'offline' };
    }
    return await syncService.syncAll();
  }, [isOnline]);

  const queueAction = useCallback(async action => {
    return await syncService.queueAction(action);
  }, []);

  const value = {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncResult,
    manualSync,
    queueAction,
    updatePendingCount,
  };

  return (
    <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
  );
}

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
}

export default OfflineContext;
